import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Send, X, Loader2, Copy, Pencil, Check } from 'lucide-react';
import { toast } from 'sonner';
import { streamChat } from '../lib/streamingChat';
import StreamingMarkdown from './StreamingMarkdown';

/**
 * StreamingChat — reusable streaming chat surface.
 *
 * Phase 51.A-Lab — drives both the Lab Assistant and any other AI
 * surface in ATDE. The global ChatWidget retains its CornerPanel chrome
 * and just embeds this component for the message thread + input.
 *
 * Props
 * ─────
 *   kind          : "operator" | "lab"   — which backend endpoint to hit
 *   sessionId     : stable session id
 *   token         : JWT for Authorization header
 *   suggestions   : optional quick-prompt buttons
 *   initialMessages: optional history hydrate
 *   onArchive     : optional callback (gets the rendered messages array)
 *   placeholder   : input placeholder text
 *   header        : optional ReactNode rendered above the thread
 *   testIdPrefix  : data-testid prefix (default "stream-chat")
 *   className     : container className
 *   minHeight     : default '40vh'
 *
 * Streaming model
 * ───────────────
 * - User submits → optimistic user bubble appended
 * - Empty assistant bubble appended; tokens incrementally append to it
 * - Stop button cancels mid-stream (AbortController on the fetch)
 */
export default function StreamingChat({
  kind = 'lab',
  sessionId,
  token,
  suggestions = [],
  initialMessages = [],
  onArchive,
  placeholder = 'Ask the assistant...',
  header,
  testIdPrefix = 'stream-chat',
  className = '',
  minHeight = '40vh',
}) {
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  // Iter 110 — in-place edit of a prior user message. On save, we
  // truncate the thread + re-stream a new assistant reply.
  const [editIndex, setEditIndex] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const cancelRef = useRef(null);
  const scrollerRef = useRef(null);

  const copyToClipboard = useCallback(async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Copied to clipboard');
    } catch { toast.error('Copy failed'); }
  }, []);

  useEffect(() => { setMessages(initialMessages); }, [sessionId]);  // eslint-disable-line

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 99999, behavior: 'smooth' });
  }, [messages, streaming]);

  const send = useCallback((text, opts = {}) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming || !token) return;
    const truncateAt = opts.truncateAt;
    if (truncateAt == null) setInput('');
    const userTurn = { role: 'user', text: msg, t: new Date().toISOString() };
    const asstTurn = { role: 'assistant', text: '', t: new Date().toISOString(), streaming: true };
    setMessages((m) => (
      truncateAt != null
        ? [...m.slice(0, truncateAt), userTurn, asstTurn]
        : [...m, userTurn, asstTurn]
    ));
    setStreaming(true);

    const handle = streamChat({
      kind,
      sessionId,
      message: msg,
      token,
      truncateAt,
      onToken: (chunk) => {
        setMessages((m) => {
          const copy = m.slice();
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant') {
            copy[copy.length - 1] = { ...last, text: last.text + chunk };
          }
          return copy;
        });
      },
      onDone: () => {
        setMessages((m) => {
          const copy = m.slice();
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant') {
            copy[copy.length - 1] = { ...last, streaming: false };
          }
          return copy;
        });
        setStreaming(false);
      },
      onError: (err) => {
        setMessages((m) => {
          const copy = m.slice();
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant') {
            copy[copy.length - 1] = {
              ...last,
              streaming: false,
              text: (last.text || '') + `\n\n_// error: ${err}_`,
            };
          }
          return copy;
        });
        setStreaming(false);
      },
    });
    cancelRef.current = handle.cancel;
  }, [input, streaming, sessionId, token, kind]);

  const stop = () => {
    cancelRef.current?.();
    cancelRef.current = null;
  };

  const beginEdit = (idx) => {
    if (streaming) return;
    const m = messages[idx];
    if (!m || m.role !== 'user') return;
    setEditIndex(idx);
    setEditDraft(m.text || '');
  };
  const cancelEdit = () => { setEditIndex(null); setEditDraft(''); };
  const saveEdit = () => {
    if (editIndex == null) return;
    const text = (editDraft || '').trim();
    if (!text) { toast.error('Message cannot be empty'); return; }
    const idx = editIndex;
    setEditIndex(null);
    setEditDraft('');
    send(text, { truncateAt: idx });
  };

  return (
    <div
      data-testid={`${testIdPrefix}-root`}
      className={`flex flex-col border border-[var(--border-default)] bg-[var(--bg-panel)] ${className}`}
      style={{ minHeight }}
    >
      {header && <div data-testid={`${testIdPrefix}-header`}>{header}</div>}

      <div
        ref={scrollerRef}
        data-testid={`${testIdPrefix}-thread`}
        className="flex-1 overflow-y-auto p-3 space-y-3 text-[12px]"
      >
        {messages.length === 0 && suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s, i) => (
              <button
                key={i}
                data-testid={`${testIdPrefix}-suggestion-${i}`}
                onClick={() => send(s)}
                className="border border-cyan-sys text-cyan-sys px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`${testIdPrefix}-msg-${m.role}`}
            className={`group ${m.role === 'user'
              ? 'border-l-2 border-cyan-sys pl-2 text-[var(--txt-primary)]'
              : 'border-l-2 border-[var(--border-default)] pl-2 text-[var(--txt-secondary)]'}`}
          >
            <div className="text-[9px] tracking-[0.25em] uppercase mb-1 text-[var(--txt-secondary)] flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                {m.role === 'user' ? 'OPERATOR' : 'ATDE-AI'}
                {m.streaming && (
                  <span className="inline-flex items-center gap-1 text-cyan-sys">
                    <Loader2 size={9} className="animate-spin" />
                    streaming...
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                {m.role === 'assistant' && !m.streaming && m.text && (
                  <button
                    data-testid={`${testIdPrefix}-copy-${i}`}
                    onClick={() => copyToClipboard(m.text)}
                    className="text-[var(--txt-muted)] hover:text-cyan-sys normal-case"
                    title="Copy to clipboard"
                    aria-label="copy reply"
                  >
                    <Copy size={11} />
                  </button>
                )}
                {m.role === 'user' && editIndex !== i && !streaming && (
                  <button
                    data-testid={`${testIdPrefix}-edit-${i}`}
                    onClick={() => beginEdit(i)}
                    className="text-[var(--txt-muted)] hover:text-cyan-sys normal-case"
                    title="Edit and regenerate"
                    aria-label="edit message"
                  >
                    <Pencil size={11} />
                  </button>
                )}
              </div>
            </div>
            <div className="markdown-body leading-relaxed">
              {m.role === 'user' && editIndex === i ? (
                <div className="flex flex-col gap-2">
                  <textarea
                    data-testid={`${testIdPrefix}-edit-input-${i}`}
                    value={editDraft}
                    autoFocus
                    onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault(); saveEdit();
                      }
                    }}
                    rows={Math.min(6, (editDraft.match(/\n/g) || []).length + 2)}
                    className="w-full bg-black border border-cyan-sys text-cyan-sys outline-none px-2 py-1 font-mono text-[12px] resize-y"
                  />
                  <div className="flex items-center gap-2 text-[9px] tracking-widest uppercase">
                    <button
                      data-testid={`${testIdPrefix}-edit-save-${i}`}
                      onClick={saveEdit}
                      className="border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black px-2 py-1 flex items-center gap-1"
                      title="save (⌘+Enter)"
                    >
                      <Check size={11} /> save &amp; regenerate
                    </button>
                    <button
                      data-testid={`${testIdPrefix}-edit-cancel-${i}`}
                      onClick={cancelEdit}
                      className="border border-[var(--border-muted)] text-[var(--txt-secondary)] hover:text-sell hover:border-sell px-2 py-1"
                      title="cancel (esc)"
                    >
                      cancel
                    </button>
                  </div>
                </div>
              ) : m.role === 'assistant' ? (
                <StreamingMarkdown text={m.text} streaming={!!m.streaming} />
              ) : m.text ? (
                <ReactMarkdown>{m.text}</ReactMarkdown>
              ) : (
                <span className="text-[var(--txt-muted)]">(empty)</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-[var(--border-default)] p-2 flex items-end gap-2">
        <textarea
          data-testid={`${testIdPrefix}-input`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            // Phase 51.M+ — Enter sends, Shift+Enter inserts a newline.
            if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
              e.preventDefault();
              if (!streaming && input.trim()) send();
            }
          }}
          placeholder={`${placeholder}  (Shift+Enter = newline)`}
          rows={1}
          className="flex-1 bg-transparent border border-[var(--border-default)] px-2 py-1.5 text-[12px] font-mono outline-none focus:border-cyan-sys resize-none max-h-40 overflow-y-auto"
          disabled={streaming}
        />
        {streaming ? (
          <button
            data-testid={`${testIdPrefix}-stop`}
            onClick={stop}
            className="ml-auto border border-sell text-sell px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-sell hover:text-black"
            title="Cancel"
          >
            <X size={14} />
          </button>
        ) : (
          <button
            data-testid={`${testIdPrefix}-send`}
            onClick={() => send()}
            disabled={!input.trim()}
            className="ml-auto border border-cyan-sys text-cyan-sys px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
            title="Send (Enter)"
          >
            <Send size={14} />
          </button>
        )}
        {onArchive && (
          <button
            data-testid={`${testIdPrefix}-archive`}
            onClick={() => onArchive(messages)}
            disabled={messages.length === 0}
            className="border border-[var(--border-default)] text-[var(--txt-secondary)] px-2 py-1.5 text-[9px] uppercase tracking-[0.2em] hover:text-cyan-sys disabled:opacity-30 disabled:cursor-not-allowed"
            title="Save to Logs"
          >
            save
          </button>
        )}
      </div>
    </div>
  );
}
